import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cx } from '../../lib/cx';
import type { ToastApi, ToastInput, ToastItem, ToastTone } from './types';

const ToastContext = createContext<ToastApi | null>(null);

const TONES: Record<ToastTone, string> = {
  success: 'border-success-700/30 bg-success-100 text-success-700',
  error: 'border-danger-600/30 bg-danger-100 text-danger-700',
  info: 'border-info-700/30 bg-info-100 text-info-700',
};

/**
 * Hosts the toasts. Mount once at the app root; messages are announced politely and errors
 * stay until dismissed.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const show = useCallback(
    (toast: ToastInput) => {
      counter.current += 1;
      const id = counter.current;
      setItems((current) => [...current, { ...toast, id }]);
      const duration = toast.durationMs ?? (toast.tone === 'error' ? 0 : 5000);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 top-4 z-50 flex w-80 flex-col gap-2"
      >
        {items.map((item) => (
          <div
            key={item.id}
            role="status"
            className={cx(
              'pointer-events-auto rounded-card border px-4 py-3 shadow-card',
              TONES[item.tone ?? 'info'],
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{item.title}</p>
                {item.description ? (
                  <p className="mt-0.5 text-xs opacity-90">{item.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                aria-label="Cerrar aviso"
                className="control-touch -my-2 -mr-3 flex w-11 shrink-0 items-center justify-center rounded-control text-sm opacity-70 hover:opacity-100"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/**
 * Access to the toast host. Throws outside a `ToastProvider` so the mistake is loud.
 *
 * @returns `show` and `dismiss`.
 */
export const useToast = (): ToastApi => {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error('useToast must be used inside ToastProvider');
  }
  return api;
};
